/**
 * Sync Engine
 * Handles synchronization when connection is restored
 * - Processes sync queue in priority order
 * - Detects and manages conflicts
 * - Provides progress updates
 */

import axios from 'axios';
import offlineDB, { 
  STORES, 
  getPendingSyncQueue, 
  updateSyncQueueItem, 
  removeFromSyncQueue,
  addConflict,
  setLastSyncTime,
  getUnsyncedAuditLogs,
  markAuditLogsSynced
} from './offlineDB';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Sync state
let isSyncing = false;
let syncProgress = { total: 0, completed: 0, failed: 0, conflicts: 0 };
const syncListeners = [];

/**
 * Subscribe to sync progress updates
 */
export const onSyncProgress = (callback) => {
  syncListeners.push(callback);
  return () => {
    const index = syncListeners.indexOf(callback);
    if (index > -1) syncListeners.splice(index, 1);
  };
};

/**
 * Notify listeners of sync progress
 */
const notifySyncProgress = () => {
  syncListeners.forEach(cb => cb({ ...syncProgress, isSyncing }));
};

/**
 * Get current sync status
 */
export const getSyncStatus = () => ({
  isSyncing,
  ...syncProgress
});

/**
 * Start synchronization process
 */
export const startSync = async (token) => {
  if (isSyncing) {
    console.log('SyncEngine: Sync already in progress');
    return { success: false, reason: 'already_syncing' };
  }

  isSyncing = true;
  syncProgress = { total: 0, completed: 0, failed: 0, conflicts: 0 };
  notifySyncProgress();

  console.log('SyncEngine: Starting synchronization...');

  try {
    // Get pending items from sync queue
    const queue = await getPendingSyncQueue();
    syncProgress.total = queue.length;
    notifySyncProgress();

    if (queue.length === 0) {
      console.log('SyncEngine: Nothing to sync');
      isSyncing = false;
      await setLastSyncTime();
      notifySyncProgress();
      return { success: true, synced: 0 };
    }

    console.log(`SyncEngine: ${queue.length} items to sync`);

    // Process queue items in order (already sorted by priority)
    for (const item of queue) {
      try {
        await processQueueItem(item, token);
        syncProgress.completed++;
      } catch (error) {
        console.error(`SyncEngine: Failed to sync item ${item.id}:`, error);
        syncProgress.failed++;
        
        // Update item with error
        await updateSyncQueueItem(item.id, {
          status: 'failed',
          error: error.message,
          last_attempt: new Date().toISOString(),
          attempts: (item.attempts || 0) + 1
        });
      }
      notifySyncProgress();
    }

    // Sync audit logs
    await syncAuditLogs(token);

    // Update last sync time
    await setLastSyncTime();

    console.log(`SyncEngine: Sync completed. Success: ${syncProgress.completed}, Failed: ${syncProgress.failed}, Conflicts: ${syncProgress.conflicts}`);

    isSyncing = false;
    notifySyncProgress();

    return {
      success: true,
      synced: syncProgress.completed,
      failed: syncProgress.failed,
      conflicts: syncProgress.conflicts
    };

  } catch (error) {
    console.error('SyncEngine: Sync failed:', error);
    isSyncing = false;
    notifySyncProgress();
    return { success: false, error: error.message };
  }
};

/**
 * Process a single queue item
 */
const processQueueItem = async (item, token) => {
  const axiosInstance = axios.create({
    baseURL: API_URL,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    },
    timeout: 30000
  });

  console.log(`SyncEngine: Processing ${item.type} for ${item.entity_id}`);

  // Check if this is a local-only item (created offline)
  const isLocalItem = item.entity_id?.startsWith('local_');

  try {
    let response;
    let serverData;

    switch (item.method) {
      case 'POST':
        // For items created offline, we need to create on server
        response = await axiosInstance.post(item.endpoint, item.data);
        serverData = response.data;
        
        // Update local item with server ID
        if (serverData.id && isLocalItem) {
          await updateLocalItemWithServerId(item, serverData);
        }
        break;

      case 'PUT':
      case 'PATCH':
        // For updates, check for conflicts first
        if (!isLocalItem) {
          const conflict = await checkForConflict(item, axiosInstance);
          if (conflict) {
            await handleConflict(item, conflict);
            syncProgress.conflicts++;
            return; // Don't proceed with update, conflict needs resolution
          }
        }
        
        response = await axiosInstance.put(item.endpoint, item.data);
        break;

      case 'DELETE':
        // For deletes, just proceed
        response = await axiosInstance.delete(item.endpoint);
        
        // Remove from local DB
        await removeLocalItem(item);
        break;

      default:
        throw new Error(`Unknown method: ${item.method}`);
    }

    // Successfully synced - remove from queue
    await removeFromSyncQueue(item.id);
    
    // Update local item as synced
    await markLocalItemSynced(item);

    console.log(`SyncEngine: Successfully synced ${item.type}`);

  } catch (error) {
    // Check if it's a conflict error (409)
    if (error.response?.status === 409) {
      await handleConflict(item, {
        server_data: error.response.data,
        conflict_type: 'UPDATE_CONFLICT'
      });
      syncProgress.conflicts++;
      return;
    }

    // Check if item no longer exists on server (404)
    if (error.response?.status === 404 && item.method !== 'POST') {
      // Item was deleted on server - remove locally
      await removeLocalItem(item);
      await removeFromSyncQueue(item.id);
      console.log(`SyncEngine: Item ${item.entity_id} no longer exists on server, removed locally`);
      return;
    }

    throw error;
  }
};

/**
 * Check if there's a conflict with server data
 */
const checkForConflict = async (item, axiosInstance) => {
  try {
    // Get current server state
    const endpoint = item.endpoint.replace(/\/[^/]+$/, `/${item.entity_id}`);
    const response = await axiosInstance.get(endpoint);
    const serverData = response.data;

    // Compare updated_at timestamps if available
    if (serverData.updated_at && item.data.updated_at) {
      const serverTime = new Date(serverData.updated_at).getTime();
      const localTime = new Date(item.data.updated_at).getTime();

      if (serverTime > localTime) {
        // Server has newer data - potential conflict
        return {
          server_data: serverData,
          conflict_type: 'UPDATE_CONFLICT'
        };
      }
    }

    return null;
  } catch (error) {
    // If we can't check, assume no conflict
    return null;
  }
};

/**
 * Handle a detected conflict
 */
const handleConflict = async (item, conflict) => {
  console.log(`SyncEngine: Conflict detected for ${item.entity_type}:${item.entity_id}`);

  // Save conflict for manual resolution
  await addConflict({
    entity_type: item.entity_type,
    entity_id: item.entity_id,
    local_data: item.data,
    server_data: conflict.server_data,
    conflict_type: conflict.conflict_type
  });

  // Update queue item as conflicted
  await updateSyncQueueItem(item.id, {
    status: 'conflict',
    conflict_data: conflict.server_data
  });
};

/**
 * Update local item with server-assigned ID
 */
const updateLocalItemWithServerId = async (item, serverData) => {
  const store = getStoreForEntityType(item.entity_type);
  if (!store) return;

  try {
    // Get the local item
    const localItem = await offlineDB.getItem(store, item.entity_id);
    if (localItem) {
      // Delete old local item
      await offlineDB.deleteItem(store, item.entity_id);
      
      // Save with server ID
      await offlineDB.saveItem(store, {
        ...localItem,
        ...serverData,
        local_id: item.entity_id, // Keep reference to local ID
        _is_synced: true,
        _synced_at: new Date().toISOString()
      });
    }
  } catch (error) {
    console.error('SyncEngine: Failed to update local item with server ID:', error);
  }
};

/**
 * Mark local item as synced
 */
const markLocalItemSynced = async (item) => {
  const store = getStoreForEntityType(item.entity_type);
  if (!store) return;

  try {
    const localItem = await offlineDB.getItem(store, item.entity_id);
    if (localItem) {
      localItem._is_synced = true;
      localItem._synced_at = new Date().toISOString();
      delete localItem._offline_created;
      delete localItem._offline_updated;
      await offlineDB.saveItem(store, localItem);
    }
  } catch (error) {
    console.error('SyncEngine: Failed to mark item as synced:', error);
  }
};

/**
 * Remove local item after sync
 */
const removeLocalItem = async (item) => {
  const store = getStoreForEntityType(item.entity_type);
  if (!store) return;

  try {
    await offlineDB.deleteItem(store, item.entity_id);
  } catch (error) {
    console.error('SyncEngine: Failed to remove local item:', error);
  }
};

/**
 * Get IndexedDB store name for entity type
 */
const getStoreForEntityType = (entityType) => {
  const map = {
    'order': STORES.ORDERS,
    'menu_item': STORES.MENU_ITEMS,
    'table': STORES.TABLES,
    'table_session': STORES.TABLE_SESSIONS,
    'booking': STORES.BOOKINGS,
    'stock': STORES.STOCK,
    'staff': STORES.STAFF,
    'waiter': STORES.WAITERS,
    'restaurant': STORES.RESTAURANT
  };
  return map[entityType] || null;
};

/**
 * Sync audit logs to server
 */
const syncAuditLogs = async (token) => {
  try {
    const unsyncedLogs = await getUnsyncedAuditLogs();
    if (unsyncedLogs.length === 0) return;

    const axiosInstance = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });

    // Send audit logs in batches
    const batchSize = 50;
    for (let i = 0; i < unsyncedLogs.length; i += batchSize) {
      const batch = unsyncedLogs.slice(i, i + batchSize);
      
      try {
        await axiosInstance.post('/audit-logs/offline-batch', { logs: batch });
        
        // Mark as synced
        const ids = batch.map(log => log.id);
        await markAuditLogsSynced(ids);
      } catch (error) {
        console.error('SyncEngine: Failed to sync audit log batch:', error);
        // Continue with next batch
      }
    }

    console.log(`SyncEngine: Synced ${unsyncedLogs.length} audit logs`);
  } catch (error) {
    console.error('SyncEngine: Failed to sync audit logs:', error);
  }
};

/**
 * Resolve a conflict manually
 */
export const resolveConflictManually = async (conflictId, resolution, token) => {
  const conflict = await offlineDB.getItem(STORES.CONFLICTS, conflictId);
  if (!conflict) {
    throw new Error('Conflict not found');
  }

  const axiosInstance = axios.create({
    baseURL: API_URL,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
  });

  let chosenData;

  switch (resolution) {
    case 'local':
      // Use local data - send to server
      chosenData = conflict.local_data;
      await axiosInstance.put(
        `/${conflict.entity_type}s/${conflict.entity_id}`,
        chosenData
      );
      break;

    case 'server':
      // Use server data - update local
      chosenData = conflict.server_data;
      const store = getStoreForEntityType(conflict.entity_type);
      if (store) {
        await offlineDB.saveItem(store, {
          ...chosenData,
          _is_synced: true
        });
      }
      break;

    case 'merged':
      // Merged data should be provided
      throw new Error('Merged resolution requires merged data');

    default:
      throw new Error(`Unknown resolution: ${resolution}`);
  }

  // Mark conflict as resolved
  await offlineDB.resolveConflict(conflictId, resolution, chosenData);

  // Remove from sync queue if present
  const queue = await getPendingSyncQueue();
  const queueItem = queue.find(q => 
    q.entity_type === conflict.entity_type && 
    q.entity_id === conflict.entity_id
  );
  if (queueItem) {
    await removeFromSyncQueue(queueItem.id);
  }

  return { success: true };
};

/**
 * Resolve conflict with merged data
 */
export const resolveConflictWithMerge = async (conflictId, mergedData, token) => {
  const conflict = await offlineDB.getItem(STORES.CONFLICTS, conflictId);
  if (!conflict) {
    throw new Error('Conflict not found');
  }

  const axiosInstance = axios.create({
    baseURL: API_URL,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
  });

  // Send merged data to server
  await axiosInstance.put(
    `/${conflict.entity_type}s/${conflict.entity_id}`,
    mergedData
  );

  // Update local cache
  const store = getStoreForEntityType(conflict.entity_type);
  if (store) {
    await offlineDB.saveItem(store, {
      ...mergedData,
      _is_synced: true
    });
  }

  // Mark conflict as resolved
  await offlineDB.resolveConflict(conflictId, 'merged', mergedData);

  // Remove from sync queue
  const queue = await getPendingSyncQueue();
  const queueItem = queue.find(q => 
    q.entity_type === conflict.entity_type && 
    q.entity_id === conflict.entity_id
  );
  if (queueItem) {
    await removeFromSyncQueue(queueItem.id);
  }

  return { success: true };
};

/**
 * Force full data refresh from server
 */
export const forceFullRefresh = async (restaurantId, token) => {
  const axiosInstance = axios.create({
    baseURL: API_URL,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
  });

  console.log('SyncEngine: Starting full data refresh...');

  try {
    // Fetch all data from server
    const [menuRes, tablesRes, stockRes, staffRes, ordersRes, bookingsRes] = await Promise.all([
      axiosInstance.get(`/restaurants/${restaurantId}/menu`),
      axiosInstance.get(`/restaurants/${restaurantId}/tables`),
      axiosInstance.get(`/restaurants/${restaurantId}/stock`),
      axiosInstance.get(`/restaurants/${restaurantId}/staff`),
      axiosInstance.get(`/restaurants/${restaurantId}/orders?limit=100`),
      axiosInstance.get(`/table-bookings?restaurant_id=${restaurantId}`)
    ]);

    // Clear and repopulate stores
    await offlineDB.clearStore(STORES.MENU_ITEMS);
    await offlineDB.clearStore(STORES.TABLES);
    await offlineDB.clearStore(STORES.STOCK);
    await offlineDB.clearStore(STORES.STAFF);

    // Save fresh data
    if (menuRes.data?.length) await offlineDB.saveItems(STORES.MENU_ITEMS, menuRes.data);
    if (tablesRes.data?.length) await offlineDB.saveItems(STORES.TABLES, tablesRes.data);
    if (stockRes.data?.length) await offlineDB.saveItems(STORES.STOCK, stockRes.data);
    if (staffRes.data?.length) await offlineDB.saveItems(STORES.STAFF, staffRes.data);
    if (ordersRes.data?.length) await offlineDB.saveItems(STORES.ORDERS, ordersRes.data);
    if (bookingsRes.data?.length) await offlineDB.saveItems(STORES.BOOKINGS, bookingsRes.data);

    await setLastSyncTime();

    console.log('SyncEngine: Full refresh completed');
    return { success: true };

  } catch (error) {
    console.error('SyncEngine: Full refresh failed:', error);
    return { success: false, error: error.message };
  }
};

export default {
  startSync,
  getSyncStatus,
  onSyncProgress,
  resolveConflictManually,
  resolveConflictWithMerge,
  forceFullRefresh
};
